define([
  'react',
  'prop-types',
  'underscore',
  'es6!sumo/util',
  'react-draggable',
], (React, PropTypes, _, Util, Draggable) => {
  const { classNames } = Util;

  return (options) => {
    options = options || {};
    const { transform } = options;

    // Simple abstraction for dragging events names.
    const eventsFor = {
      touch: {
        start: 'touchstart',
        move: 'touchmove',
        stop: 'touchend'
      },
      mouse: {
        start: 'mousedown',
        move: 'mousemove',
        stop: 'mouseup'
      }
    };

    function addEvent(el, event, handler) {
      if (!el) { return; }
      if (el.attachEvent) {
        el.attachEvent('on' + event, handler);
      } else if (el.addEventListener) {
        el.addEventListener(event, handler, true);
      } else {
        // $FlowIgnore: Doesn't think elements are indexable
        el['on' + event] = handler;
      }
    }

    function removeEvent(el, event, handler) {
      if (!el) { return; }
      if (el.detachEvent) {
        el.detachEvent('on' + event, handler);
      } else if (el.removeEventListener) {
        el.removeEventListener(event, handler, true);
      } else {
       //$FlowIgnore: Doesn't think elements are indexable
       el['on' + event] = null;
      }
    }

    return (Component) => {
      // This HOC renders the component it wraps without a wrapper and purely
      // keeps track of mouse state. OnDrag, it gives the delta, initial position
      // and current position of the mouse pointer relative to the client viewport
      // and not any component.
      class Drag extends React.Component {
        static propTypes = {
          onDrag: PropTypes.func,
          isDragDisabled: PropTypes.bool,
        };

        static defaultProps = {
          isDragDisabled: false,
        };

        constructor(props) {
          super(props);

          this.dragEventsFor = eventsFor.mouse;
          this.isDragging = false;
          this.lastX = 0;
          this.lastY = 0;

          this.onStartDrag = this.onStartDrag.bind(this);
          this.onDrag = this.onDrag.bind(this);
          this.onStopDrag = this.onStopDrag.bind(this);
          this.onTouchStart = this.onTouchStart.bind(this);
          this.onTouchEnd = this.onTouchEnd.bind(this);
          this.onMouseDown = this.onMouseDown.bind(this);
          this.onMouseUp = this.onMouseUp.bind(this);
        }

        componentWillUnmount() {
          removeEvent(document, eventsFor.mouse.move, this.onDrag);
          removeEvent(document, eventsFor.touch.move, this.onDrag);
          removeEvent(document, eventsFor.mouse.stop, this.onStopDrag);
          removeEvent(document, eventsFor.touch.stop, this.onStopDrag);
        }

        onStartDrag(e) {
          // don't allow event to start drag on nested components
          e.stopPropagation();

          const { onStart } = this.props;

          if (this.isDragging) return;
          // Only start dragging for left mouse clicks.
          if (typeof e.button === 'number' && e.button !== 0) return;

          e.preventDefault();

          // keep the offset of the pointer in the dragging element's coordinate system
          const boundingClientRect = e.target.getBoundingClientRect();
          const innerOffsetX = e.clientX - boundingClientRect.left;
          const innerOffsetY = e.clientY - boundingClientRect.top;

          this.isDragging = true;
          this.initialX = e.clientX;
          this.initialY = e.clientY;
          this.lastX = e.clientX;
          this.lastY = e.clientY;
          this.innerOffsetX = innerOffsetX;
          this.innerOffsetY = innerOffsetY;

          if (onStart) onStart({ x: e.clientX, y: e.clientY });

          addEvent(document, this.dragEventsFor.move, this.onDrag);
          addEvent(document, this.dragEventsFor.stop, this.onStopDrag);
        }

        onDrag(e) {
          const { onDrag } = this.props;
          const { clientX, clientY } = e;
          const deltaX = clientX - this.lastX;
          const deltaY = clientY - this.lastY;
          const data = {
            x: clientX,
            y: clientY,
            initialX: this.initialX,
            initialY: this.initialY,
            deltaX,
            deltaY,
          };

          // Only update if there is a deltaX or deltaY.
          if (onDrag && (deltaX || deltaY)) onDrag(data);

          this.lastX = clientX;
          this.lastY = clientY;

          e.stopImmediatePropagation();
        }

        onStopDrag(e) {
          // If we aren't dragging, don't do anything.
          if (!this.isDragging) return;

          const { onStop } = this.props;
          const { clientX, clientY } = e;
          const boundingClientRect = e.target.getBoundingClientRect();
          const targetOffsetX = e.clientX - boundingClientRect.left;
          const targetOffsetY = e.clientY - boundingClientRect.top;
          const data = {
            innerOffsetX: this.innerOffsetX,
            innerOffsetY: this.innerOffsetY,
            offsetX: targetOffsetX,
            offsetY: targetOffsetY,
          };

          e.preventDefault();

          this.isDragging = false;

          if (onStop) onStop(data);

          removeEvent(document, this.dragEventsFor.move, this.onDrag);
          removeEvent(document, this.dragEventsFor.stop, this.onStopDrag);
        }

        onTouchStart(e) {
          this.dragEventsFor = eventsFor.touch;
          this.onStartDrag(e);
        }

        onTouchEnd(e) {
          this.dragEventsFor = eventsFor.touch;
          this.onStopDrag(e);
        }

        onMouseDown(e) {
          this.dragEventsFor = eventsFor.mouse;
          this.onStartDrag(e);
        }

        onMouseUp(e) {
          this.dragEventsFor = eventsFor.mouse;
          this.onStopDrag(e);
        }

        render() {
          const propsCopy = _.clone(this.props) || {};

          if (!propsCopy.isDragDisabled) {
            const className = classNames(this.props.className, {
              'sumome-react-wysiwyg-draggable': true,
            });

            propsCopy.style = propsCopy.style || {};
            propsCopy.style.touchAction = 'none';

            propsCopy.draggableEventHandlers = {
              onTouchStart: this.onTouchStart,
              onTouchEnd: this.onTouchEnd,
              onMouseDown: this.onMouseDown,
              onMouseUp: this.onMouseUp,
            }
          }

          return <Component {...propsCopy} />;
        }
      }

      return Drag;
    };
  };
});
